import { Head, router, useForm } from '@inertiajs/react'
import {
  ClipboardEvent,
  FormEvent,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react'
import { HiArrowCircleRight } from 'react-icons/hi'
import AuthLayout from '../../components/AuthLayout'

const OTP_LENGTH = 6
const RESEND_SECONDS = 30

export default function Verify({ email }: { email: string }) {
  const form = useForm({ email, code: '' })
  const [digits, setDigits] = useState<string[]>(() => Array(OTP_LENGTH).fill(''))

  // Keep form.code in sync with digits as a joined string.
  useEffect(() => {
    form.setData('code', digits.join(''))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [digits])

  const submit = (e: FormEvent) => {
    e.preventDefault()
    form.post('/login/verify')
  }

  const code = digits.join('')
  const canSubmit = code.length === OTP_LENGTH && !form.processing

  return (
    <>
      <Head title="Enter code" />
      <AuthLayout>
        <h1 className="text-center font-display text-2xl font-medium tracking-tight text-midnight-green">
          Enter OTP Verification Code
        </h1>
        <p className="mt-2 text-center font-display text-base text-gray-700">
          Enter the code we sent to your email at {email}
        </p>

        <form onSubmit={submit} className="mt-5 space-y-5">
          <OtpInput
            value={digits}
            onChange={setDigits}
            onComplete={(finalCode) => {
              form.transform((data) => ({ ...data, code: finalCode }))
              form.post('/login/verify')
            }}
          />

          {form.errors.code && (
            <p className="text-center text-sm text-rose-600">{form.errors.code}</p>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            className="flex w-full items-center justify-center gap-2 rounded-md bg-ink px-8 py-4 font-display text-base font-medium text-white transition hover:bg-midnight-green disabled:opacity-50"
          >
            {form.processing ? 'Verifying…' : 'Submit'}
            <HiArrowCircleRight className="h-5 w-5" aria-hidden="true" />
          </button>

          <ResendRow email={email} />
        </form>
      </AuthLayout>
    </>
  )
}

// ────────────────────────────────────────────────────────────────
// OtpInput — 6 single-digit boxes with auto-advance, backspace, and paste.
// Kept inline because it has exactly one caller per the project rule.
// ────────────────────────────────────────────────────────────────

type OtpInputProps = {
  value: string[]
  onChange: (next: string[]) => void
  onComplete?: (code: string) => void
}

function OtpInput({ value, onChange, onComplete }: OtpInputProps) {
  const refs = useRef<Array<HTMLInputElement | null>>([])

  const setDigit = (index: number, digit: string) => {
    const next = [...value]
    next[index] = digit
    onChange(next)
    if (digit && index < OTP_LENGTH - 1) {
      refs.current[index + 1]?.focus()
    }
    if (digit && index === OTP_LENGTH - 1 && next.every((d) => d !== '')) {
      onComplete?.(next.join(''))
    }
  }

  const handleChange = (index: number, raw: string) => {
    const digit = raw.replace(/\D/g, '').slice(-1)
    setDigit(index, digit)
  }

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (value[index]) {
        const next = [...value]
        next[index] = ''
        onChange(next)
        return
      }
      if (index > 0) {
        const next = [...value]
        next[index - 1] = ''
        onChange(next)
        refs.current[index - 1]?.focus()
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      refs.current[index - 1]?.focus()
    } else if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      refs.current[index + 1]?.focus()
    }
  }

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH)
    if (!pasted) return
    e.preventDefault()
    const next = Array(OTP_LENGTH).fill('')
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i]
    onChange(next)
    const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1)
    refs.current[focusIndex]?.focus()
    if (pasted.length === OTP_LENGTH) onComplete?.(next.join(''))
  }

  return (
    <div className="flex gap-2 px-5">
      {value.map((digit, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el
          }}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          className="h-[60px] min-w-0 flex-1 rounded-md border border-gray-300 bg-white text-center font-display text-2xl font-medium text-gray-900 shadow-sm focus:border-midnight-green focus:outline-none focus:ring-1 focus:ring-midnight-green"
        />
      ))}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────
// ResendRow — "Resend code  0:30" countdown, becomes active at 0.
// ────────────────────────────────────────────────────────────────

function ResendRow({ email }: { email: string }) {
  const [secondsLeft, setSecondsLeft] = useState(RESEND_SECONDS)

  useEffect(() => {
    if (secondsLeft <= 0) return
    const id = window.setTimeout(() => setSecondsLeft((s) => s - 1), 1000)
    return () => window.clearTimeout(id)
  }, [secondsLeft])

  const formatted = `0:${secondsLeft.toString().padStart(2, '0')}`

  const resend = () => {
    router.post(
      '/login',
      { email },
      {
        onSuccess: () => setSecondsLeft(RESEND_SECONDS),
        preserveScroll: true,
      },
    )
  }

  return (
    <div className="flex items-center justify-center gap-1">
      <span className="font-display text-base font-medium text-gray-600">
        Resend code
      </span>
      {secondsLeft > 0 ? (
        <span className="font-display text-base font-medium text-midnight-green">
          {formatted}
        </span>
      ) : (
        <button
          type="button"
          onClick={resend}
          className="font-display text-base font-medium text-midnight-green underline"
        >
          Resend
        </button>
      )}
    </div>
  )
}
